import { useMemo, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Segmented } from 'antd';

import { Checkbox, Empty, Input } from '../ui';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';
import {
  demChon,
  doiHangLoat,
  locNhom,
  nhomDaDoi,
  soSanhQuyen,
  tachNhan,
  type BoLocChon,
  type NhomChon,
} from '../../lib/chon-quyen';

export interface ChonQuyenChiaDoiProps {
  nhom: NhomChon[];
  daChon: ReadonlySet<string>;
  /**
   * Tập quyền đã lưu, để báo thay đổi chưa lưu và chấm "đã đổi" ở nhóm. Bỏ trống khi tạo mới: chưa có bản lưu thì
   * không có gì để so, và ẩn cả dòng báo lẫn chấm.
   */
  daLuu?: ReadonlySet<string>;
  onDoi: (moi: Set<string>) => void;
  /** Lỗi `422` theo mã quyền — hiện ngay dưới đúng ô. */
  loiTheoQuyen?: Record<string, string>;
  voHieu?: boolean;
}

/**
 * Chọn quyền kiểu chia đôi: danh sách nhóm (module) bên trái, quyền của nhóm đang xem bên phải.
 *
 * Thay cho cột dọc mở hết khi tổ chức có nhiều module: chỉ đọc một nhóm mỗi lúc, nhưng vẫn thấy tiến độ của mọi
 * nhóm (`4/12`) và nhóm nào đã đổi so với bản lưu. Một ô tìm lọc cả hai phía (khớp tên nhóm, mô tả hoặc mã quyền, không
 * dấu); bộ lọc Đã chọn / Chưa chọn để rà một vai trò.
 *
 * Ô chọn cả nhóm chỉ tác động lên các quyền **đang hiện** sau lọc, như `LuoiChonQuyen`: gõ `xoá` rồi chọn cả nhóm là
 * chọn đúng các quyền xoá, không lén chọn thêm quyền bị ẩn. Số `chọn/tổng` đếm trên cả nhóm gốc.
 *
 * Dưới `md` hai phía xếp dọc, danh sách nhóm thành khối cuộn ngắn. Chỉ lo phần trình bày: tải, lỗi, rỗng của danh mục
 * do màn gọi tự lo.
 */
export function ChonQuyenChiaDoi({ nhom, daChon, daLuu, onDoi, loiTheoQuyen = {}, voHieu }: ChonQuyenChiaDoiProps) {
  const { t } = useT();
  const [tuKhoa, setTuKhoa] = useState('');
  const [boLoc, setBoLoc] = useState<BoLocChon>('tat-ca');
  const [khoaChon, setKhoaChon] = useState<string | null>(null);

  const nhomHien = useMemo(() => locNhom(nhom, tuKhoa, boLoc, daChon), [nhom, tuKhoa, boLoc, daChon]);
  const daDoi = useMemo(() => (daLuu ? nhomDaDoi(nhom, daLuu, daChon) : new Set<string>()), [nhom, daLuu, daChon]);
  const lech = daLuu ? soSanhQuyen(daLuu, daChon) : { them: 0, bo: 0 };

  // Nhóm đang xem rơi ra ngoài kết quả lọc thì lấy nhóm đầu tiên còn hiện, không cần effect.
  const dangXem = nhomHien.find((n) => n.khoa === khoaChon) ?? nhomHien[0];
  const gocDangXem = dangXem ? (nhom.find((g) => g.khoa === dangXem.khoa) ?? dangXem) : undefined;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="space-y-2">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-content-muted" />}
          placeholder={t('chonQuyen.timPlaceholder')}
          aria-label={t('chonQuyen.timNhan')}
          value={tuKhoa}
          onChange={(e) => setTuKhoa(e.target.value)}
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Segmented<BoLocChon>
            size="small"
            aria-label={t('chonQuyen.boLoc')}
            value={boLoc}
            onChange={setBoLoc}
            options={[
              { value: 'tat-ca', label: t('chonQuyen.locTatCa') },
              { value: 'da-chon', label: t('chonQuyen.locDaChon') },
              { value: 'chua-chon', label: t('chonQuyen.locChuaChon') },
            ]}
          />
          {daLuu && (
            <span role="status" className="text-xs tabular-nums text-content-muted">
              {lech.them + lech.bo > 0
                ? t('chonQuyen.chuaLuu', { them: lech.them, bo: lech.bo })
                : t('chonQuyen.chuaCoThayDoi')}
            </span>
          )}
        </div>
      </div>

      {!dangXem || !gocDangXem ? (
        // Rỗng vì tìm hoặc lọc: không mô tả, không nút hành động.
        <Empty className="rounded-lg border border-dashed border-default py-8" title={t('chonQuyen.khongKhop')} />
      ) : (
        <div className="grid min-h-0 flex-1 gap-3 md:grid-cols-[15rem_minmax(0,1fr)]">
          <nav
            aria-label={t('chonQuyen.danhSachNhom')}
            className="max-h-44 min-h-0 overflow-y-auto rounded-lg border border-default bg-surface md:max-h-none"
          >
            <ul className="divide-y divide-default">
              {nhomHien.map((n) => {
                const goc = nhom.find((g) => g.khoa === n.khoa) ?? n;
                const soChonGoc = demChon(goc.quyen, daChon);
                const soChonHien = demChon(n.quyen, daChon);
                const dangChon = n.khoa === dangXem.khoa;
                return (
                  <li key={n.khoa} className={cn('flex items-center gap-2 px-3 py-2', dangChon && 'bg-primary-50')}>
                    <Checkbox
                      checked={soChonHien === n.quyen.length}
                      indeterminate={soChonHien > 0 && soChonHien < n.quyen.length}
                      disabled={voHieu}
                      aria-label={t('chonQuyen.chonCaNhom', { nhom: n.nhan })}
                      onChange={() => onDoi(doiHangLoat(daChon, n.quyen))}
                    />
                    <button
                      type="button"
                      aria-current={dangChon ? 'true' : undefined}
                      onClick={() => setKhoaChon(n.khoa)}
                      className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-100"
                    >
                      <span
                        className={cn(
                          'min-w-0 truncate text-sm',
                          dangChon ? 'font-medium text-primary-700' : 'text-content-primary',
                        )}
                        title={n.nhan}
                      >
                        {n.nhan}
                      </span>
                      <span className="flex shrink-0 items-center gap-1.5 text-2xs tabular-nums text-content-muted">
                        {daDoi.has(n.khoa) && (
                          <>
                            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary-600" />
                            <span className="sr-only">{t('chonQuyen.nhomDaDoi')}</span>
                          </>
                        )}
                        {soChonGoc}/{goc.quyen.length}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <section
            aria-label={dangXem.nhan}
            className="min-h-0 overflow-y-auto rounded-lg border border-default bg-surface"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-default bg-subtle px-3 py-2">
              <Checkbox
                className="min-w-0"
                checked={demChon(dangXem.quyen, daChon) === dangXem.quyen.length}
                indeterminate={
                  demChon(dangXem.quyen, daChon) > 0 && demChon(dangXem.quyen, daChon) < dangXem.quyen.length
                }
                disabled={voHieu}
                aria-label={t('chonQuyen.chonCaNhom', { nhom: dangXem.nhan })}
                onChange={() => onDoi(doiHangLoat(daChon, dangXem.quyen))}
              >
                <span className="text-sm font-medium text-content-primary">{dangXem.nhan}</span>
              </Checkbox>
              <span className="shrink-0 text-2xs tabular-nums text-content-muted">
                {t('chonQuyen.daChonTrongNhom', { chon: demChon(gocDangXem.quyen, daChon), tong: gocDangXem.quyen.length })}
              </span>
            </div>
            <ul>
              {dangXem.quyen.map((q) => {
                const { chinh, phu } = tachNhan(q.nhan);
                const loiO = loiTheoQuyen[q.code];
                return (
                  <li key={q.code} className="border-b border-default px-3 py-2 last:border-b-0">
                    <Checkbox
                      checked={daChon.has(q.code)}
                      disabled={voHieu}
                      onChange={() => {
                        const moi = new Set(daChon);
                        if (moi.has(q.code)) moi.delete(q.code);
                        else moi.add(q.code);
                        onDoi(moi);
                      }}
                    >
                      <span className="block text-sm text-content-primary">{chinh}</span>
                      {phu && <span className="block text-xs text-content-muted">{phu}</span>}
                    </Checkbox>
                    {loiO && (
                      <p role="alert" className="mt-1 pl-6 text-xs text-danger-600">
                        {loiO}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      )}
    </div>
  );
}
